<template>
  <div class="flex">
    <!-- Tour Guide -->
    <TourGuide
      v-if="areVerticalBarsDisplayed && !isMobileViewport"
      ref="tourGuideRef"
      @finish="handleTourFinish"
    ></TourGuide>
    
    <!-- Onboarding Modal -->
    <TourOnboardingModal 
      v-if="areVerticalBarsDisplayed && showOnboardingModal && !isMobileViewport"
      :show="showOnboardingModal" 
      @start="handleOnboardingStart"
      @skip="handleOnboardingSkip"
    />
    
    <div class="relative">
      <CookieConsent></CookieConsent>
      <SubscriptionFlow
        v-if="showSubscriptionFlow"
        @close="showSubscriptionFlow = false"
        @success="handleSubscriptionSuccess"
      />
    </div>

    <!-- Modal envoi badge (store global - toujours visible) -->
    <BasicSmModal
      v-if="sendModalTemplate"
      @close="closeSendModal"
      :noCloseOption="true"
    >
      <TemplateSend
        :templateList="sendModalTemplateList"
        :template="sendModalTemplate"
        :type="sendModalTemplate.data.badgeCategory"
        @close="closeSendModal"
        @refreshTemplate="refreshSendModalTemplate"
        @refreshTemplateList="refreshSendModalTemplateList"
        click-outside-ignore-templatesender-target
      />
    </BasicSmModal>

    <div
      v-if="showPasskeyReminderPopup && isMobileViewport"
      class="fixed inset-0 z-[10080] md:hidden"
    >
      <div
        class="absolute inset-0 bg-slate-950/35 backdrop-blur-[5px] dark:bg-black/65"
        @click="dismissPasskeyReminderPopup"
      ></div>
      <div
        class="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-[1.5rem] border-t border-slate-200 bg-white px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 text-slate-950 shadow-2xl dark:border-slate-800 dark:bg-slate-950 dark:text-slate-50"
        @click.stop
      >
        <div class="mx-auto mb-3 h-1 w-11 rounded-full bg-slate-300 dark:bg-slate-700"></div>

        <div class="flex flex-col items-center text-center">
          <div class="relative mb-2 flex h-16 w-16 items-center justify-center">
            <div class="absolute inset-0 rounded-full bg-indigo-100/80 blur-xl dark:bg-indigo-500/15"></div>
            <div class="relative flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-400/20">
              <span translate="no" class="material-icons-round text-3xl">security</span>
              <span translate="no" class="material-icons-round absolute text-xl">fingerprint</span>
            </div>
          </div>

          <h3 class="text-[22px] font-extrabold leading-tight text-slate-950 dark:text-white">
            {{ $t('passkeyReminder.mobileTitle') }}
          </h3>
          <p class="mt-1.5 max-w-[18rem] text-sm leading-5 text-slate-600 dark:text-slate-300">
            {{ $t('passkeyReminder.mobileBody') }}
          </p>
        </div>

        <div class="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <div class="flex gap-3 border-b border-slate-100 p-3 dark:border-slate-800">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-indigo-600 dark:border-slate-700 dark:bg-slate-950 dark:text-indigo-300">
              <span translate="no" class="material-icons-round text-2xl">key</span>
            </span>
            <div class="min-w-0 text-left">
              <p class="text-sm font-bold text-slate-950 dark:text-white">{{ $t('passkeyReminder.mobileFeature1Title') }}</p>
              <p class="mt-0.5 text-xs leading-4 text-slate-600 dark:text-slate-300">{{ $t('passkeyReminder.mobileFeature1Body') }}</p>
            </div>
          </div>
          <div class="flex gap-3 border-b border-slate-100 p-3 dark:border-slate-800">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-indigo-600 dark:border-slate-700 dark:bg-slate-950 dark:text-indigo-300">
              <span translate="no" class="material-icons-round text-2xl">bolt</span>
            </span>
            <div class="min-w-0 text-left">
              <p class="text-sm font-bold text-slate-950 dark:text-white">{{ $t('passkeyReminder.mobileFeature2Title') }}</p>
              <p class="mt-0.5 text-xs leading-4 text-slate-600 dark:text-slate-300">{{ $t('passkeyReminder.mobileFeature2Body') }}</p>
            </div>
          </div>
          <div class="flex gap-3 p-3">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-indigo-600 dark:border-slate-700 dark:bg-slate-950 dark:text-indigo-300">
              <span translate="no" class="material-icons-round text-2xl">devices</span>
            </span>
            <div class="min-w-0 text-left">
              <p class="text-sm font-bold text-slate-950 dark:text-white">{{ $t('passkeyReminder.mobileFeature3Title') }}</p>
              <p class="mt-0.5 text-xs leading-4 text-slate-600 dark:text-slate-300">{{ $t('passkeyReminder.mobileFeature3Body') }}</p>
            </div>
          </div>
        </div>

        <div class="mt-4 space-y-2">
          <button
            type="button"
            class="flex h-11 w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 text-sm font-bold text-white shadow-lg shadow-indigo-600/25 transition-transform active:scale-[0.99]"
            @click="openPasskeySetupFromReminder"
          >
            {{ $t('passkeyReminder.ctaSetup') }}
          </button>
          <button
            type="button"
            class="flex h-11 w-full items-center justify-center rounded-xl border border-indigo-500 bg-white text-sm font-bold text-indigo-600 transition-colors active:bg-indigo-50 dark:border-indigo-400 dark:bg-slate-950 dark:text-indigo-300 dark:active:bg-indigo-500/10"
            @click="dismissPasskeyReminderPopup"
          >
            {{ $t('passkeyReminder.ctaLater') }}
          </button>
        </div>

        <p class="mx-auto mt-3 max-w-[17rem] text-center text-xs leading-4 text-slate-500 dark:text-slate-400">
          {{ $t('passkeyReminder.mobileFootnote') }}
        </p>
      </div>
    </div>

    <BasicSmModal
      v-if="showPasskeyReminderPopup && !isMobileViewport"
      @close="dismissPasskeyReminderPopup"
      centered
      :noCloseOption="true"
    >
      <div class="-mx-4 sm:-mx-5 lg:-mx-6 -my-3 sm:-my-4 relative overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200/80 dark:border-indigo-300/10 bg-gradient-to-b from-white via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-900 dark:to-[#0b1326] shadow-[0_22px_48px_rgba(2,6,23,0.45)]">
        <button
          type="button"
          class="absolute top-3.5 right-3.5 z-20 h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/70 dark:hover:bg-slate-700/60 transition-colors"
          :aria-label="$t('passkeyReminder.close')"
          @click="dismissPasskeyReminderPopup"
        >
          <span translate="no" class="material-icons-round text-xl leading-none">close</span>
        </button>

        <div class="px-5 sm:px-6 pt-5 sm:pt-6 pb-5 sm:pb-6 flex flex-col items-center text-center gap-4">
          <div class="inline-flex items-center gap-1.5 rounded-full border border-slate-300/70 dark:border-indigo-200/15 bg-white/85 dark:bg-slate-800/55 px-3 py-1">
            <span translate="no" class="material-icons-round text-sm text-indigo-600 dark:text-indigo-300">verified_user</span>
            <span class="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-600 dark:text-indigo-200/90">
              {{ $t('passkeyReminder.badge') }}
            </span>
          </div>

          <div class="relative w-full max-w-[320px] aspect-[16/9] rounded-xl border border-slate-300/70 dark:border-indigo-200/10 bg-gradient-to-tr from-slate-100/60 via-white to-indigo-100/40 dark:from-[#0c1732] dark:via-[#101d3f] dark:to-[#1a2d57] overflow-hidden">
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,rgba(99,102,241,0.25),transparent_50%),radial-gradient(circle_at_88%_80%,rgba(56,189,248,0.18),transparent_45%)]"></div>
            <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg border border-slate-300/70 dark:border-white/10 bg-white/80 dark:bg-slate-900/55 backdrop-blur-sm px-4 py-3 shadow-xl">
              <div class="flex items-center gap-3">
                <div class="flex flex-col items-center gap-1">
                  <span translate="no" class="material-icons-round text-2xl text-indigo-600 dark:text-indigo-300">fingerprint</span>
                  <div class="h-1 w-8 rounded-full bg-indigo-300/50 dark:bg-indigo-400/30"></div>
                </div>
                <div class="h-10 w-px bg-slate-300/80 dark:bg-white/15"></div>
                <div class="flex flex-col items-center gap-1">
                  <span translate="no" class="material-icons-round text-2xl text-slate-500 dark:text-slate-300">devices</span>
                  <div class="h-1 w-8 rounded-full bg-slate-300/70 dark:bg-slate-500/40"></div>
                </div>
              </div>
            </div>
            <div class="absolute bottom-2.5 right-5 h-10 w-10 rounded-lg border border-slate-300/60 dark:border-white/10 bg-white/85 dark:bg-slate-800/70 shadow-lg flex items-center justify-center rotate-6">
              <span translate="no" class="material-icons-round text-xl text-indigo-600 dark:text-indigo-300">lock</span>
            </div>
          </div>

          <div class="space-y-2 max-w-sm">
            <h3 class="text-xl sm:text-2xl leading-tight font-bold text-slate-900 dark:text-slate-100">
              {{ $t('passkeyReminder.title') }}
            </h3>
            <p class="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
              {{ $t('passkeyReminder.body') }}
            </p>
          </div>

          <div class="w-full max-w-sm space-y-2.5 pt-0.5">
            <button
              type="button"
              class="w-full py-3 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-semibold shadow-[0_8px_18px_rgba(79,70,229,0.28)] hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2"
              @click="openPasskeySetupFromReminder"
            >
              <span translate="no" class="material-icons-round text-lg">fingerprint</span>
              {{ $t('passkeyReminder.ctaSetup') }}
            </button>
            <button
              type="button"
              class="w-full py-3 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              @click="dismissPasskeyReminderPopup"
            >
              {{ $t('passkeyReminder.ctaLater') }}
            </button>
          </div>

          <p class="max-w-sm text-[11px] sm:text-xs text-slate-500 dark:text-slate-400">
            {{ $t('passkeyReminder.footnote') }}
          </p>
        </div>
      </div>
    </BasicSmModal>

    <!-- Sidebar + bouton replier : le bouton est centré sur le bord droit du menu (quel que soit w-40 / w-52 / réduit) -->
    <div
      v-if="areVerticalBarsDisplayed"
      class="relative z-[100] hidden min-h-screen flex-none md:block"
      style="pointer-events: auto;"
    >
      <div
        class="min-h-screen relative overflow-auto transition-all duration-300 ease-in-out"
        :class="isNavbarCollapsed ? 'w-12 md:w-16' : 'w-40 md:w-52'"
      >
        <div class="absolute w-full h-full" style="pointer-events: auto;">
          <nav-bar :key="currentIssuerDid"></nav-bar>
        </div>
      </div>

      <div
        class="pointer-events-none absolute left-full top-[1.2rem] z-[101] -translate-x-1/2"
      >
        <button
          type="button"
          class="pointer-events-auto flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white transition-all duration-200 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
          @click="toggleCollapse"
          :title="isNavbarOpen ? 'Collapse menu' : 'Expand menu'"
          :aria-expanded="isNavbarOpen"
          :aria-label="isNavbarOpen ? 'Collapse menu' : 'Expand menu'"
        >
          <span
            translate="no"
            class="material-icons-round select-none text-[17px] leading-none text-slate-700 dark:text-slate-200"
            aria-hidden="true"
          >
            {{ isNavbarOpen ? 'chevron_left' : 'chevron_right' }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-if="areVerticalBarsDisplayed && mobileNavOpen"
      class="fixed inset-0 z-[10050] md:hidden"
      :class="isDarkMode ? 'bg-slate-950' : 'bg-white'"
      style="pointer-events: auto;"
    >
      <div
        class="absolute inset-0 overflow-hidden"
        :class="isDarkMode ? 'bg-slate-950' : 'bg-white'"
      >
        <div class="absolute right-5 top-6 z-10">
          <button
            type="button"
            class="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-slate-700 transition-colors hover:bg-slate-200 dark:border-white/10 dark:bg-white/10 dark:text-slate-200 dark:hover:bg-white/15"
            aria-label="Fermer le menu"
            @click="mobileNavOpen = false"
          >
            <span translate="no" class="material-icons-round text-xl">close</span>
          </button>
        </div>
        <div class="h-full overflow-y-auto">
          <nav-bar :key="`mobile-${currentIssuerDid}`" force-expanded compact-drawer></nav-bar>
        </div>
      </div>
    </div>

    <div
      class="flex-1 relative"
      :class="{
        'min-h-screen': !isMarketingPublicRoute,
        'w-full': isMarketingPublicRoute,
        'pt-16': areVerticalBarsDisplayed,
      }"
    >
      <div
        v-if="areVerticalBarsDisplayed"
        class="absolute bg-gray-100 dark:bg-slate-800 w-full border-b border-gray-200 dark:border-slate-700 left-0 top-0 h-16 flex items-center justify-center px-6 z-[25]"
      >
        <button
          type="button"
          class="absolute left-3 flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 md:hidden"
          aria-label="Ouvrir le menu"
          @click="mobileNavOpen = true"
        >
          <span translate="no" class="material-icons-round text-xl">menu</span>
        </button>
        <!-- Titre à gauche (masqué sur mobile si prekey actif) -->
        <div class="absolute left-14 flex items-center gap-4 md:left-6" :class="currentIssuerInfo && currentIssuerInfo.state === 'prekey' ? 'hidden lg:flex' : 'flex'">
          <div>
            <h1 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ $t('navigation.communityStudio') }}
            </h1>
            <p v-if="apiVersion" class="text-xs text-slate-500 dark:text-slate-400">
              v{{ require('../package.json').version }}-{{ apiVersion }}
          </p>
          </div>
        </div>
        
        <!-- Badge Mode Analyse centré (responsive) - cliquable → Créer une communauté -->
        <button
          type="button"
          id="tg-prekey-banner"
          v-if="currentIssuerInfo && currentIssuerInfo.state === 'prekey'"
          @click="router.push('/community-create')"
          class="flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg max-w-[calc(100%-8rem)] sm:max-w-none cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
        >
          <span translate="no" class="material-icons-round text-base text-indigo-600 dark:text-indigo-400 flex-shrink-0" style="font-family: 'Material Icons Round'">hub</span>
          <span class="text-[10px] sm:text-xs text-indigo-600 dark:text-indigo-400 font-semibold tracking-wide truncate">
            {{ $t('navigation.prekeyMode') }}
          </span>
        </button>
        
        <!-- Rappel plan + bouton thème à droite (style objet visible comme prekey banner) -->
        <div class="absolute right-6 flex items-center gap-2">
          <button
            type="button"
            id="tg-plan-reminder"
            @click="!isPlanReminderLoading && router.push('/settings/subscription')"
            :class="[
              'hidden sm:flex items-center gap-1.5 h-8 px-2 sm:px-2.5 rounded-lg cursor-pointer transition-colors outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
              isPlanReminderLoading
                ? 'bg-slate-100 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 cursor-default pointer-events-none'
                : isFreePlan
                ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50 hover:border-green-300 dark:hover:border-green-600 focus:ring-green-500/50'
                : 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 hover:border-indigo-300 dark:hover:border-indigo-600 focus:ring-indigo-500/50'
            ]"
            :title="$t('header.viewSubscription')"
          >
            <template v-if="isPlanReminderLoading">
              <span class="w-3.5 h-3.5 rounded-full bg-slate-300 dark:bg-slate-500 animate-pulse" aria-hidden="true"></span>
              <span class="w-20 h-2.5 rounded bg-slate-300 dark:bg-slate-500 animate-pulse" aria-hidden="true"></span>
            </template>
            <template v-else>
              <span translate="no" :class="['material-icons-round !text-[14px] leading-none shrink-0 inline-flex h-3.5 w-3.5 items-center justify-center', isFreePlan ? 'text-green-600 dark:text-green-400' : 'text-indigo-600 dark:text-indigo-400']">card_membership</span>
              <span :class="['text-[10px] sm:text-[11px] font-semibold tracking-wide whitespace-nowrap', isFreePlan ? 'text-green-600 dark:text-green-400' : 'text-indigo-600 dark:text-indigo-400']">
                {{ planReminderLabel }}
              </span>
            </template>
          </button>
          <button
            type="button"
            @click="toggleDarkMode"
            class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm transition-all duration-200 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:bg-slate-600"
            :title="$t('header.changeThemeTitle')"
            :aria-label="$t('header.changeThemeTitle')"
          >
            <i
              v-if="!isDarkMode"
              class="fas fa-adjust text-sm leading-none"
              aria-hidden="true"
            ></i>
            <i
              v-else
              class="fas fa-sun text-sm leading-none"
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </div>

      <div
        v-if="areVerticalBarsDisplayed && isSetting"
        class="absolute left-0 top-16 z-[24] w-full min-w-0 border-b border-gray-200 bg-white/95 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/95 md:hidden"
      >
        <nav
          class="flex w-full min-w-0 overflow-x-auto px-3 py-1.5 [scrollbar-gutter:stable] sm:px-4"
          aria-label="Navigation des réglages"
        >
          <div class="inline-flex min-w-max items-center justify-start gap-1.5 sm:gap-2">
            <button
              v-for="tab in settingsHeaderTabs"
              :key="tab.path"
              type="button"
              class="inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-md border-2 px-2 py-1 text-left text-xs font-semibold transition-all duration-200 active:scale-[0.98]"
              :class="
                isSettingsHeaderTabActive(tab)
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                  : 'border-transparent bg-slate-50 text-slate-700 hover:border-slate-200 hover:bg-slate-100 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-700/70'
              "
              :aria-current="isSettingsHeaderTabActive(tab) ? 'page' : undefined"
              @click="router.push(tab.path)"
            >
              <span
                translate="no"
                class="material-icons-round text-[16px] leading-none"
                :class="isSettingsHeaderTabActive(tab) ? 'text-blue-600 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'"
              >
                {{ tab.icon }}
              </span>
              <span class="max-w-[8.5rem] truncate">{{ tab.label }}</span>
            </button>
          </div>
        </nav>
        <nav
          v-if="isLegalRoute"
          class="border-t border-gray-200 px-3 py-2 dark:border-slate-700 sm:px-4"
          aria-label="Navigation des pages légales"
        >
          <div class="grid grid-cols-2 gap-1.5 sm:grid-cols-4 sm:gap-2">
            <button
              v-for="tab in legalSubTabs"
              :key="tab.path"
              type="button"
              class="inline-flex min-h-9 w-full items-center justify-center gap-1 rounded-md border-2 px-2 py-1.5 text-center text-xs font-semibold transition-all duration-200 active:scale-[0.98]"
              :class="
                isLegalSubTabActive(tab.path)
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200'
                  : 'border-transparent bg-slate-50 text-slate-700 hover:border-slate-200 hover:bg-slate-100 dark:bg-slate-900/60 dark:text-slate-300 dark:hover:border-slate-700 dark:hover:bg-slate-700/70'
              "
              :aria-current="isLegalSubTabActive(tab.path) ? 'page' : undefined"
              @click="router.push(tab.path)"
            >
              <span
                translate="no"
                class="material-icons-round text-[15px] leading-none"
                :class="isLegalSubTabActive(tab.path) ? 'text-blue-600 dark:text-blue-300' : 'text-slate-500 dark:text-slate-400'"
              >
                {{ tab.icon }}
              </span>
              <span class="min-w-0 truncate">{{ tab.label }}</span>
            </button>
          </div>
        </nav>
      </div>

      <!-- Popup rappel abonnement (style carte plan, couleur selon plan) -->
      <div
        v-if="showPlanReminderPopup"
        class="fixed inset-0 z-[10060] bg-black/50 dark:bg-black/60 flex items-center justify-center p-4"
        @click.self="dismissPlanPopup"
      >
        <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 max-w-md w-full p-6 relative">
          <button
            type="button"
            class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            :aria-label="$t('planPopup.close')"
            @click="dismissPlanPopup"
          >
            <span class="text-xl leading-none">×</span>
          </button>

          <h3 class="text-xl font-bold text-slate-900 dark:text-white pr-10">
            {{ planPopupCard.subtitle }}
          </h3>
          <p class="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-0.5">
            {{ planPopupCard.cardTitle }}
          </p>
          <p class="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">
            {{ planPopupCard.description }}
          </p>

          <div
            class="inline-block mt-3 px-3 py-1 rounded-lg text-sm font-bold text-white"
            :class="isFreePlan ? 'bg-green-600 dark:bg-green-500' : 'bg-indigo-600 dark:bg-indigo-500'"
          >
            {{ planPopupCard.badge }}
          </div>

          <p
            class="mt-3 text-2xl font-bold"
            :class="isFreePlan ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'"
          >
            {{ planPopupCard.priceLabel }}
          </p>

          <button
            type="button"
            class="mt-4 w-full py-3 rounded-xl text-white font-semibold text-sm transition-all"
            :class="isFreePlan
              ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 dark:from-green-600 dark:to-green-700'
              : 'bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 dark:from-indigo-600 dark:to-indigo-700'"
            @click="goToSubscription"
          >
            {{ $t('planPopup.cta') }}
          </button>

          <ul class="mt-5 space-y-2">
            <li
              v-for="(feature, i) in planPopupCard.features"
              :key="i"
              class="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300"
            >
              <icon
                name="check"
                class="w-5 h-5 flex-shrink-0 mt-0.5"
                :class="isFreePlan ? 'fill-green-500 dark:fill-green-400' : 'fill-indigo-500 dark:fill-indigo-400'"
              />
              <span>{{ feature }}</span>
            </li>
          </ul>

          <p class="mt-4 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            {{ $t('planPopup.disclaimer') }}
          </p>

          <button
            type="button"
            class="mt-4 w-full text-center text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 underline transition-colors"
            @click="dismissPlanPopup"
          >
            {{ $t('planPopup.dontShowAgain') }}
          </button>
        </div>
      </div>
      <!-- Pages marketing : scroll document (fix WebView FB/TikTok Android) -->
      <div
        v-if="isMarketingPublicRoute"
        class="relative w-full min-h-screen"
      >
        <MarketingNavbar />
        <router-view :key="`${route.path}-${currentLocale}`" v-slot="slotProps">
          <component :is="slotProps.Component"></component>
        </router-view>
      </div>
      <div
        v-else
        ref="mainScrollContainerRef"
        class="relative w-full h-full overflow-auto bg-white dark:bg-slate-900"
      >
        <div
          class="absolute w-full h-full"
          :class="contentAreaPaddingClass"
          style="z-index: 1; pointer-events: auto;"
        >
          <router-view :key="route.path.startsWith('/templates') ? `templates-${currentLocale}` : `${route.path}-${currentLocale}`" v-slot="slotProps">
            <component
              v-if="isSettingsStandaloneRoute"
              :is="slotProps.Component"
            ></component>
            <div
              v-else
              class="h-full"
              :class="{ 'border-l border-gray-200 dark:border-slate-700': isSetting }"
            >
              <component :is="slotProps.Component"></component>
            </div>
          </router-view>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, inject, watch, computed, onMounted, onBeforeUnmount, provide, defineAsyncComponent, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EventsBus from './plugins/eventBus';
import { syncDocumentLang, getBrowserLocaleOnly } from './plugins/i18n.js';
import { normalizeClientPlan, BASIC } from './constants/subscriptionPlans';
import { isLightBootPublicPath, isMarketingPublicPath } from './utils/marketingPages';
import {
  ensureLandingStylesheets,
  removeLandingStylesheets,
} from './utils/landingStylesheets';

const MarketingNavbar = defineAsyncComponent(() =>
  import(/* webpackChunkName: "marketing-navbar" */ './components/marketing/MarketingNavbar.vue')
);

const NavBar = defineAsyncComponent(() => import(/* webpackChunkName: "nav-bar" */ './components/NavBar.vue'));
const CookieConsent = defineAsyncComponent(() => import(/* webpackChunkName: "cookie-consent" */ './components/CookieConsent.vue'));
const SubscriptionFlow = defineAsyncComponent(() => import(/* webpackChunkName: "subscription-flow" */ './components/SubscriptionFlow.vue'));
const TourOnboardingModal = defineAsyncComponent(() => import(/* webpackChunkName: "tour" */ './components/TourOnboardingModal.vue'));
const TourGuide = defineAsyncComponent(() => import(/* webpackChunkName: "tour" */ './components/TourGuide.vue'));
const BasicSmModal = defineAsyncComponent(() => import(/* webpackChunkName: "modal" */ './components/BasicSmModal.vue'));
const TemplateSend = defineAsyncComponent(() => import(/* webpackChunkName: "template-send" */ './components/templates/TemplateSend.vue'));

export default {
  setup() {
    const utils = inject('utils');
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const cookies = inject('cookies');
    const api = inject('api');
    const { locale, t } = useI18n();
    const { eventBus } = EventsBus();


    const currentIssuerDid = computed(() => store.getters.getCurrentIssuerDid);
    const currentAddress = computed(() => store.getters.getCurrentAddress);
    const currentIssuerInfo = computed(
      () => store.getters.getCurrentIssuerInfo
    );
    const sessionInfo = computed(() => store.getters.getSessionInfo);
    const isAdmin = computed(() => store.getters.isAdmin);
    const isWeb2Connexion = computed(() => store.getters.isWeb2Connexion);
    const areVerticalBarsDisplayed = computed(
      () => store.getters.areVerticalBarsDisplayed
    );
    const isMarketingPublicRoute = computed(() =>
      isMarketingPublicPath(route.path)
    );
    const MARKETING_SCROLL_CLASS = 'marketing-page-scroll';
    const syncMarketingPageScrollClass = (path = route.path) => {
      if (typeof document === 'undefined') return;
      const root = document.documentElement;
      if (isMarketingPublicPath(path)) {
        root.classList.add(MARKETING_SCROLL_CLASS);
      } else {
        root.classList.remove(MARKETING_SCROLL_CLASS);
      }
    };
    const isNavbarCollapsed = computed(() => store.getters.isNavbarCollapsed);
    const isNavbarOpen = computed(() => !isNavbarCollapsed.value);
    const mobileNavOpen = ref(false);
    const isMobileViewport = ref(
      typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
    );
    const syncMobileViewport = () => {
      if (typeof window === 'undefined') return;
      isMobileViewport.value = window.matchMedia('(max-width: 767px)').matches;
      if (isMobileViewport.value) {
        showOnboardingModal.value = false;
        if (store.getters.isTourOngoing) {
          store.commit('setTourOngoing', false);
          currentTourType.value = null;
        }
      }
    };

    const basicTemplateList = computed(
      () => store.getters.getBasicTemplateList
    );
    const communityTemplateList = computed(
      () => store.getters.getCommunityTemplateList
    );
    const participationTemplateList = computed(
      () => store.getters.getParticipationTemplateList
    );
    const ticketTemplateList = computed(
      () => store.getters.getTicketTemplateList
    );
    const membershipTemplateList = computed(
      () => store.getters.getMembershipTemplateList
    );
    const roleTemplateList = computed(() => store.getters.getRoleTemplateList);
    const templateListLoading = computed(
      () => store.getters.getTemplateListLoading
    );

    const sendModalTemplate = computed(() => store.getters.getSendModalTemplate);
    const sendModalTemplateList = computed(() => {
      const t = sendModalTemplate.value;
      if (!t) return [];
      const type = t.data?.badgeCategory || 'Basic';
      let list = [];
      if (type === 'Basic') {
        list = [
          ...(basicTemplateList.value || []),
          ...(communityTemplateList.value || []),
          ...(participationTemplateList.value || []),
          ...(ticketTemplateList.value || []),
        ];
      } else if (type === 'Role') list = roleTemplateList.value || [];
      else if (type === 'Participation') list = participationTemplateList.value || [];
      else if (type === 'Ticket') list = ticketTemplateList.value || [];
      else if (type === 'Community') list = communityTemplateList.value || [];
      return [...list].reverse();
    });

    const PASSKEY_REMINDER_AUTH_FREE_PATHS = [
      '/signin',
      '/signup',
      '/protected',
      '/expired',
      '/auth',
    ];
    const passkeyEnabled =
      process.env.VUE_APP_PASSKEY_ENABLED === 'true';
    const passkeySupportedBrowser =
      typeof window !== 'undefined' &&
      window.isSecureContext === true &&
      !!window.PublicKeyCredential &&
      !String(navigator?.userAgent || '')
        .toLowerCase()
        .includes('cursor');
    const showPasskeyReminderPopup = ref(false);
    const passkeyReminderLoading = ref(false);
    const passkeyReminderCheckedFingerprint = ref('');
    const pendingPasskeyReminder = ref(null);
    const PASSKEY_REMINDER_SESSION_KEY = 'passkeyReminderSeen';
    const PASSKEY_REMINDER_FORCE_QUERY_KEY = 'forcePasskeyReminder';

    function getSessionFingerprint(session) {
      const accountId = session?.studioAccountId || '';
      const exp = session?.exp || '';
      const email = session?.connexionEmail || '';
      return `${accountId}|${exp}|${email}`;
    }

    function hasSeenPasskeyReminder(fingerprint) {
      if (!fingerprint) return false;
      try {
        return (
          sessionStorage.getItem(`${PASSKEY_REMINDER_SESSION_KEY}:${fingerprint}`) ===
          '1'
        );
      } catch (_error) {
        return false;
      }
    }

    function markPasskeyReminderSeen(fingerprint) {
      if (!fingerprint) return;
      try {
        sessionStorage.setItem(
          `${PASSKEY_REMINDER_SESSION_KEY}:${fingerprint}`,
          '1'
        );
      } catch (_error) {
        /* ignore */
      }
    }

    function isPasskeyReminderBlocked() {
      return (
        showOnboardingModal.value ||
        store.getters.isTourOngoing ||
        showSubscriptionFlow.value ||
        showPlanReminderPopup.value ||
        !!sendModalTemplate.value
      );
    }

    function showOrQueuePasskeyReminder({ fingerprint = '', force = false } = {}) {
      if (isPasskeyReminderBlocked()) {
        pendingPasskeyReminder.value = { fingerprint, force };
        return false;
      }

      if (!force) {
        markPasskeyReminderSeen(fingerprint);
      }
      pendingPasskeyReminder.value = null;
      showPasskeyReminderPopup.value = true;
      return true;
    }

    function flushPendingPasskeyReminder() {
      if (!pendingPasskeyReminder.value) return;
      if (PASSKEY_REMINDER_AUTH_FREE_PATHS.includes(route.path)) return;
      if (showPasskeyReminderPopup.value || passkeyReminderLoading.value) return;
      showOrQueuePasskeyReminder(pendingPasskeyReminder.value);
    }

    async function maybeShowPasskeyReminderPopup() {
      if (!passkeyEnabled || !passkeySupportedBrowser) return;
      if (showPasskeyReminderPopup.value || passkeyReminderLoading.value) return;
      if (PASSKEY_REMINDER_AUTH_FREE_PATHS.includes(route.path)) return;
      if (String(route.query?.[PASSKEY_REMINDER_FORCE_QUERY_KEY] || '') === '1') {
        showOrQueuePasskeyReminder({ force: true });
        return;
      }

      const session = sessionInfo.value;
      if (!session || session.isTmp || !session.web2 || !session.connexionEmail) return;

      const fingerprint = getSessionFingerprint(session);
      if (!fingerprint || passkeyReminderCheckedFingerprint.value === fingerprint) return;
      if (hasSeenPasskeyReminder(fingerprint)) {
        passkeyReminderCheckedFingerprint.value = fingerprint;
        return;
      }

      passkeyReminderLoading.value = true;
      try {
        const response = await api.getPasskeyCredentials();
        const count = Array.isArray(response?.data?.credentials)
          ? response.data.credentials.length
          : 0;
        passkeyReminderCheckedFingerprint.value = fingerprint;
        if (count === 0) {
          showOrQueuePasskeyReminder({ fingerprint });
        }
      } catch (error) {
        if ([400, 401, 403, 404].includes(error?.response?.status)) {
          passkeyReminderCheckedFingerprint.value = fingerprint;
        }
      } finally {
        passkeyReminderLoading.value = false;
      }
    }

    function clearPasskeyReminderForceQuery() {
      const query = { ...(route.query || {}) };
      if (!(PASSKEY_REMINDER_FORCE_QUERY_KEY in query)) return;
      delete query[PASSKEY_REMINDER_FORCE_QUERY_KEY];
      router.replace({ path: route.path, query }).catch(() => {});
    }

    function dismissPasskeyReminderPopup() {
      showPasskeyReminderPopup.value = false;
      clearPasskeyReminderForceQuery();
    }

    function openPasskeySetupFromReminder() {
      showPasskeyReminderPopup.value = false;
      const query = { ...route.query };
      if (PASSKEY_REMINDER_FORCE_QUERY_KEY in query) {
        delete query[PASSKEY_REMINDER_FORCE_QUERY_KEY];
      }
      router.push({ path: '/settings/account', query: { ...query, tab: 'security' } });
    }

    // Rappel plan (header) + popup dashboard — définis tôt pour exposition au template
    const isPlanReminderLoading = computed(() => {
      const info = currentIssuerInfo.value;
      if (!info || typeof info !== 'object') return true;
      const sub = info.subscription;
      if (!sub || typeof sub !== 'object') return true;
      return !(
        sub.plan ||
        sub.tier ||
        sub.type ||
        sub.expirationDate ||
        sub.customerId ||
        sub.isSubscribed === true ||
        sub.isSubscribed === false
      );
    });
    const planReminderLabel = computed(() => {
      const sub = currentIssuerInfo.value?.subscription;
      const plan = normalizeClientPlan(sub?.plan ?? sub?.tier);
      const planLabel = t('header.' + plan);
      return t('header.planReminder', { tier: planLabel });
    });
    const isFreePlan = computed(() => {
      const sub = currentIssuerInfo.value?.subscription;
      const plan = normalizeClientPlan(sub?.plan ?? sub?.tier);
      return plan === BASIC && !sub?.type && !sub?.isSubscribed;
    });
    const planPopupTitle = computed(() => (isFreePlan.value ? t('planPopup.titleFree') : t('planPopup.titlePaid')));
    const planPopupMessage = computed(() => (isFreePlan.value ? t('planPopup.messageFree') : t('planPopup.messagePaid')));
    const planPopupCard = computed(() => {
      const free = isFreePlan.value;
      if (free) {
        return {
          cardTitle: t('settings.manageSubscription.freeTierTitle'),
          subtitle: t('settings.manageSubscription.currentPlanBadge'),
          description: t('settings.manageSubscription.freePlanDescription'),
          badge: t('settings.manageSubscription.freeTierTitle'),
          priceLabel: t('settings.manageSubscription.freeTierTitle'),
          features: [
            t('settings.manageSubscription.freePlanFeature1'),
            t('settings.manageSubscription.freePlanFeature2'),
            t('settings.manageSubscription.freePlanFeature3'),
            t('settings.manageSubscription.freePlanFeature4'),
            t('settings.manageSubscription.freePlanFeature5'),
            t('settings.manageSubscription.freePlanFeature6'),
            t('settings.manageSubscription.freePlanFeature7'),
          ],
        };
      }
      return {
        cardTitle: t('planPopup.cardTitlePaid'),
        subtitle: t('planPopup.subtitlePaid'),
        description: t('planPopup.messagePaid'),
        badge: t('planPopup.badgePaid'),
        priceLabel: t('planPopup.pricePaid'),
        features: [
          t('planPopup.feature1Paid'),
          t('planPopup.feature2Paid'),
          t('planPopup.feature3Paid'),
        ],
      };
    });
    const PLAN_POPUP_SESSION_KEY = 'planReminderPopupShown';
    const PLAN_POPUP_DISABLED = true; // remettre à false pour réafficher la popup plan plus tard
    const showPlanReminderPopup = ref(false);
    function setPlanPopupShown() {
      try { sessionStorage.setItem(PLAN_POPUP_SESSION_KEY, '1'); } catch (_) {}
    }
    function dismissPlanPopup() {
      showPlanReminderPopup.value = false;
      setPlanPopupShown();
    }
    function goToSubscription() {
      showPlanReminderPopup.value = false;
      setPlanPopupShown();
      router.push('/settings/subscription');
    }

    function closeSendModal() {
      store.commit('closeSendModal');
    }
    function refreshSendModalTemplate(id) {
      store.dispatch('refreshTemplate', { id });
    }
    function refreshSendModalTemplateList() {
      const type = store.state.sendModalTemplate?.data?.badgeCategory;
      if (type) {
        store.dispatch('refreshTemplateList', { type });
      }
      // Rafraîchir aussi les autres types pour que les compteurs (Badges Actifs, etc.) soient à jour partout
      store.dispatch('refreshTemplateList', { type: 'Community' });
      store.dispatch('refreshTemplateList', { type: 'Participation' });
      store.dispatch('refreshTemplateList', { type: 'Ticket' });
      store.dispatch('refreshTemplateList', { type: 'Membership' });
      store.dispatch('refreshTemplateList', { type: 'Role' });
    }

    const apiVersion = ref(null);
    const showSubscriptionFlow = ref(false);
    const isDarkMode = ref(false);
    const displayAccount = ref(process.env.VUE_APP_DISPLAY_ACCOUNT === 'true');
    const displayAccessRole = ref(
      process.env.VUE_APP_DISPLAY_ACCESS_ROLE === 'true'
    );
    const displayPromote = ref(process.env.VUE_APP_DISPLAY_PROMOTE === 'true');
    const displayApiKey = ref(process.env.VUE_APP_DISPLAY_APIKEY === 'true');
    const displaySubscription = ref(
      process.env.VUE_APP_DISPLAY_SUBSCRIPTION === 'true'
    );
    const bootRecoveryStep = ref('');
    let bootRecoveryTimer = null;
    const BOOT_RECOVERY_TIMEOUT_MS = (() => {
      const n = Number(process.env.VUE_APP_BOOT_RECOVERY_TIMEOUT_MS || 15000);
      if (!Number.isFinite(n) || n <= 0) return 15000;
      return Math.max(8000, Math.min(Math.round(n), 60000));
    })();
    const SESSION_RECOVERY_TIMEOUT_MS = (() => {
      const n = Number(process.env.VUE_APP_SESSION_RECOVERY_TIMEOUT_MS || 12000);
      if (!Number.isFinite(n) || n <= 0) return 12000;
      return Math.max(5000, Math.min(Math.round(n), 45000));
    })();

    function hideInitialLoader() {
      const loader = document.getElementById('app-loader');
      if (loader) {
        loader.style.display = 'none';
      }
    }

    function startBootRecoveryWatchdog(step) {
      bootRecoveryStep.value = step || 'unknown';
      if (bootRecoveryTimer) clearTimeout(bootRecoveryTimer);
      bootRecoveryTimer = setTimeout(() => {
        console.warn('[BOOT] watchdog timeout', {
          step: bootRecoveryStep.value,
          timeoutMs: BOOT_RECOVERY_TIMEOUT_MS,
        });
      }, BOOT_RECOVERY_TIMEOUT_MS);
    }

    function stopBootRecoveryWatchdog() {
      if (bootRecoveryTimer) {
        clearTimeout(bootRecoveryTimer);
        bootRecoveryTimer = null;
      }
    }

    async function withTimeout(promise, timeoutMs, timeoutMessage) {
      let timeoutId = null;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(timeoutMessage)),
          timeoutMs
        );
      });
      try {
        return await Promise.race([promise, timeoutPromise]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }

    const showOnboardingModal = ref(false);
    const tourGuideRef = ref(null);
    const currentTourType = ref(null);
    
    // Computed simple pour la locale (pour éviter les warnings Vue)
    const currentLocale = computed(() => String(locale.value));

    const normalizedRoutePath = computed(() =>
      String(route.path || '').toLowerCase()
    );

    const isSetting = computed(() => {
      return normalizedRoutePath.value.includes('/settings');
    });
    const isPrekey = computed(() => currentIssuerInfo.value?.state === 'prekey');
    const settingsHeaderTabs = computed(() =>
      [
        {
          path: '/settings/profile',
          label: t('navigation.profileInformationButton'),
          icon: 'manage_accounts',
          visible: isAdmin.value,
        },
        {
          path: '/settings/subscription',
          label: t('navigation.subscriptionButton'),
          icon: 'credit_card',
          visible: isAdmin.value && displaySubscription.value,
        },
        {
          path: '/settings/plans',
          label: t('navigation.subscriptionPlansButton'),
          icon: 'layers',
          visible: isAdmin.value && displaySubscription.value,
        },
        {
          path: '/settings/account',
          label: t('navigation.accountButton'),
          icon: 'account_circle',
          visible: isAdmin.value && isWeb2Connexion.value && displayAccount.value,
        },
        {
          path: '/settings/access',
          label: t('navigation.accessRolesButton'),
          icon: 'group',
          visible: isAdmin.value && displayAccessRole.value && !isPrekey.value,
        },
        {
          path: '/settings/api-key',
          label: t('navigation.apiKeyButton'),
          icon: 'key',
          visible:
            isAdmin.value &&
            displayApiKey.value &&
            currentIssuerInfo.value?.isKms &&
            !isPrekey.value,
        },
        {
          path: '/settings/login-method',
          label: t('navigation.loginMethodButton'),
          icon: 'lock',
          visible: isAdmin.value && !isWeb2Connexion.value && !isPrekey.value,
        },
        {
          path: '/settings/language',
          label: t('navigation.languageButton'),
          icon: 'language',
          visible: true,
        },
        {
          path: '/settings/promote',
          label: t('navigation.promoteButton'),
          icon: 'verified',
          visible: isAdmin.value && displayPromote.value && !isPrekey.value,
        },
        {
          path: '/settings/legal',
          label: t('navigation.legalMenuButton'),
          icon: 'article',
          visible: true,
          matches: ['/settings/legal', '/settings/terms', '/settings/privacy', '/settings/cookies'],
        },
        {
          path: '/settings/advanced',
          label: t('settings.manageAdvanced.title'),
          icon: 'tune',
          visible: isAdmin.value && !isPrekey.value,
        },
      ].filter((tab) => tab.visible)
    );

    function isSettingsHeaderTabActive(tab) {
      const path = normalizedRoutePath.value;
      const matches = Array.isArray(tab.matches) ? tab.matches : [tab.path];
      return matches.some((match) => path === String(match).toLowerCase());
    }

    const LEGAL_ROUTE_PATHS = [
      '/settings/terms',
      '/settings/privacy',
      '/settings/legal',
      '/settings/cookies',
    ];

    const isLegalRoute = computed(() =>
      LEGAL_ROUTE_PATHS.includes(normalizedRoutePath.value)
    );

    const legalSubTabs = computed(() => [
      {
        path: '/settings/terms',
        label: t('navigation.termsButton'),
        icon: 'description',
      },
      {
        path: '/settings/privacy',
        label: t('navigation.privacyButton'),
        icon: 'shield',
      },
      {
        path: '/settings/legal',
        label: t('navigation.legalButton'),
        icon: 'info',
      },
      {
        path: '/settings/cookies',
        label: t('navigation.cookieButton'),
        icon: 'cookie',
      },
    ]);

    function isLegalSubTabActive(path) {
      return normalizedRoutePath.value === String(path).toLowerCase();
    }

    const isSettingsStandaloneRoute = computed(() => {
      return [
        '/settings/profile',
        '/settings/account',
        '/settings/api-key',
        '/settings/language',
        '/settings/subscription',
        '/settings/advanced',
        '/settings/terms',
        '/settings/privacy',
        '/settings/legal',
        '/settings/cookies',
        '/settings/promote',
      ].includes(normalizedRoutePath.value);
    });

    // Le sous-menu settings a toujours une largeur fixe md:w-44 (11rem)
    // Mobile: la navigation réglages est horizontale sous le header.
    // Desktop: le sous-menu réglages reste en colonne à gauche.
    const contentAreaPaddingClass = computed(() => {
      if (!isSetting.value) return '';
      const mobileSettingsNavPadding = isLegalRoute.value
        ? 'pt-[7.5rem]'
        : 'pt-[3.25rem]';
      return `${mobileSettingsNavPadding} md:pt-0 md:pl-[11rem]`;
    });

    const mainScrollContainerRef = ref(null);

    function isSettingsPath(path) {
      return String(path || '').toLowerCase().includes('/settings');
    }

    function resetMainScrollPosition() {
      nextTick(() => {
        const el = mainScrollContainerRef.value;
        if (el && typeof el.scrollTo === 'function') {
          el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
          return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    }

    watch(
      () => route.path,
      (path, previousPath) => {
        if (!previousPath || path === previousPath) return;
        if (isSettingsPath(path) && isSettingsPath(previousPath)) {
          resetMainScrollPosition();
        }
      }
    );

    watch(
      () => ({ path: route.path, displayed: areVerticalBarsDisplayed.value }),
      ({ path, displayed }) => {
        mobileNavOpen.value = false;
        if (PLAN_POPUP_DISABLED) return;
        if (!displayed) return;
        // try {
        //   if (sessionStorage.getItem(PLAN_POPUP_SESSION_KEY)) return;
        // } catch (_) {}
        if (path === '/' || path === '/templates/basic' || path === '/brand-analyzer') {
          showPlanReminderPopup.value = true;
        }
      },
      { immediate: true }
    );

    watch(
      () => [
        sessionInfo.value?.studioAccountId,
        sessionInfo.value?.connexionEmail,
        sessionInfo.value?.exp,
        route.path,
      ],
      () => {
        maybeShowPasskeyReminderPopup();
      },
      { immediate: true }
    );

    watch(
      () => [
        showOnboardingModal.value,
        store.getters.isTourOngoing,
        showSubscriptionFlow.value,
        showPlanReminderPopup.value,
        !!sendModalTemplate.value,
      ],
      () => {
        flushPendingPasskeyReminder();
      }
    );

    // Toggle dark mode
    const toggleDarkMode = () => {
      isDarkMode.value = !isDarkMode.value;
      if (isDarkMode.value) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('darkMode', 'true');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('darkMode', 'false');
      }
    };

    // Check initial dark mode preference
    const checkDarkMode = () => {
      const savedDarkMode = localStorage.getItem('darkMode');
      if (savedDarkMode === 'true') {
        isDarkMode.value = true;
        document.documentElement.classList.add('dark');
      } else {
        // Default to light mode (white theme)
        isDarkMode.value = false;
        document.documentElement.classList.remove('dark');
        // Only set localStorage if it's explicitly set to false, otherwise leave it null for first visit
        if (savedDarkMode === 'false') {
          localStorage.setItem('darkMode', 'false');
        }
      }
    };

    // Listen for subscription flow open event
    const handleOpenSubscriptionFlow = () => {
      showSubscriptionFlow.value = true;
    };

    watch(
      () => route.path,
      (path) => {
        syncMarketingPageScrollClass(path);
      },
      { immediate: true }
    );

    watch(
      isMarketingPublicRoute,
      (isMarketing) => {
        if (isMarketing) ensureLandingStylesheets();
        else removeLandingStylesheets();
      },
      { immediate: true }
    );

    watch(
      () => route.path,
      (path, previousPath) => {
        if (!isMarketingPublicPath(path)) return;
        if (previousPath && isMarketingPublicPath(previousPath) && path !== previousPath) {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      }
    );

    onMounted(() => {
      syncMobileViewport();
      syncMarketingPageScrollClass(route.path);
      window.addEventListener('openSubscriptionFlow', handleOpenSubscriptionFlow);
      window.addEventListener('resize', syncMobileViewport, { passive: true });
    });

    onBeforeUnmount(() => {
      window.removeEventListener('openSubscriptionFlow', handleOpenSubscriptionFlow);
      window.removeEventListener('resize', syncMobileViewport);
      stopBootRecoveryWatchdog();
      if (typeof document !== 'undefined') {
        document.documentElement.classList.remove(MARKETING_SCROLL_CLASS);
      }
    });

    const handleSubscriptionSuccess = () => {
      showSubscriptionFlow.value = false;
      // Refresh issuer info to get updated subscription status
      store.dispatch('getIssuerInfo');
    };

    // Tour Onboarding handlers
    const handleOnboardingStart = () => {
      if (isMobileViewport.value) {
        showOnboardingModal.value = false;
        return;
      }
      showOnboardingModal.value = false;
      
      // 🧪 MODE TEST : Sauvegarde désactivée temporairement
      // TODO: Réactiver après les tests
      // const currentDid = currentIssuerDid.value;
      // if (currentDid) {
      //   localStorage.setItem(`tour_completed_onboarding_${currentDid}`, 'true');
      // }
      
      // Lancer le tour en 4 étapes focalisées
      currentTourType.value = 'onboarding';
      store.commit('setTourOngoing', true);
      setTimeout(() => {
        if (isMobileViewport.value || !tourGuideRef.value) {
          store.commit('setTourOngoing', false);
          currentTourType.value = null;
          return;
        }
        tourGuideRef.value.start([
          {
            content: t('tourGuide.onboarding.step1Text'),
            title: t('tourGuide.onboarding.step1Title'),
            id: 'tg-social-networks-list',
          },
          {
            content: t('tourGuide.onboarding.step2Text'),
            title: t('tourGuide.onboarding.step2Title'),
            id: 'tg-sentiment-score',
          },
          {
            content: t('tourGuide.onboarding.step3Text'),
            title: t('tourGuide.onboarding.step3Title'),
            id: 'tg-prekey-banner',
          },
          {
            content: t('tourGuide.onboarding.step4Text'),
            title: t('tourGuide.onboarding.step4Title'),
            id: 'tg-create-community-btn',
          },
        ]);
      }, 300);
    };

    const handleOnboardingSkip = () => {
      showOnboardingModal.value = false;
      
      // Sauvegarder que l'utilisateur a skip le tour
      const currentDid = currentIssuerDid.value;
      if (currentDid) {
        localStorage.setItem(`tour_completed_onboarding_${currentDid}`, 'true');
      }
    };

    const handleTourFinish = () => {
      // Tour terminé
      store.commit('setTourOngoing', false);
      
      const currentDid = currentIssuerDid.value;
      const forceRelaunch = process.env.VUE_APP_FORCE_COMMUNITY_TOUR === 'true';
      
      if (currentDid && !forceRelaunch) {
        if (currentTourType.value === 'templateCreate') {
          localStorage.setItem(`tour_completed_template_create_${currentDid}`, 'true');
        } else if (currentTourType.value === 'communityCreated') {
          localStorage.setItem(`tour_completed_community_created_${currentDid}`, 'true');
        } else if (currentTourType.value === 'onboarding') {
          localStorage.setItem(`tour_completed_onboarding_${currentDid}`, 'true');
        }
      }
      currentTourType.value = null;
    };

    function startTemplateCreateTour() {
      if (isMobileViewport.value) return;
      if (!tourGuideRef.value) return;
      currentTourType.value = 'templateCreate';
      store.commit('setTourOngoing', true);
      tourGuideRef.value.start([
        { content: t('tourGuide.templateCreate.step1Text'), title: t('tourGuide.templateCreate.step1Title'), id: 'tg-createTemplate-title' },
        { content: t('tourGuide.templateCreate.step2Text'), title: t('tourGuide.templateCreate.step2Title'), id: 'tg-createTemplate-description' },
        { content: t('tourGuide.templateCreate.step3Text'), title: t('tourGuide.templateCreate.step3Title'), id: 'tg-createTemplate-image' },
        { content: t('tourGuide.templateCreate.step4Text'), title: t('tourGuide.templateCreate.step4Title'), id: 'tg-createTemplate-aiGenerate' },
        { content: t('tourGuide.templateCreate.step5Text'), title: t('tourGuide.templateCreate.step5Title'), id: 'tg-createTemplate-texture' },
        { content: t('tourGuide.templateCreate.step5ShapeText'), title: t('tourGuide.templateCreate.step5ShapeTitle'), id: 'tg-createTemplate-shape' },
        { content: t('tourGuide.templateCreate.step6Text'), title: t('tourGuide.templateCreate.step6Title'), id: 'tg-createTemplate-create' },
      ]);
    }

    provide('startTemplateCreateTour', startTemplateCreateTour);

    // Watcher pour déclencher le tour après création de communauté
    watch([currentIssuerInfo, route], () => {
      if (isMobileViewport.value) {
        showOnboardingModal.value = false;
        return;
      }
      const currentDid = currentIssuerDid.value;
      if (!currentDid) return;

      const issuerState = currentIssuerInfo.value?.state;
      const forceRelaunch = process.env.VUE_APP_FORCE_COMMUNITY_TOUR === 'true';
      
      // Vérifier si un tour doit être lancé après création de communauté
      const tourPendingKey = `tour_pending_community_created_${currentDid}`;
      const tourPending = localStorage.getItem(tourPendingKey);
      const tourCompletedKey = `tour_completed_community_created_${currentDid}`;
      const tourCompleted = localStorage.getItem(tourCompletedKey);
      
      // Lancer le tour si :
      // 1. Le marqueur pending existe (juste après création), OU
      // 2. Mode test activé ET tour pas encore complété
      const shouldLaunchTour = tourPending || (forceRelaunch && !tourCompleted);
      
      // Accepter 'kmskey' ou 'validated' comme état valide
      const isValidState = issuerState === 'kmskey' || issuerState === 'validated';
      
      // Lancer le tour peu importe la page (pas de condition sur route.path)
      if (shouldLaunchTour && isValidState) {
        // En mode test, ne pas nettoyer le marqueur pending
        if (!forceRelaunch) {
          localStorage.removeItem(tourPendingKey);
        }
        
        // Lancer le tour après un délai pour laisser la page se charger
        currentTourType.value = 'communityCreated';
        store.commit('setTourOngoing', true);
        setTimeout(() => {
          if (isMobileViewport.value || !tourGuideRef.value) {
            store.commit('setTourOngoing', false);
            currentTourType.value = null;
            return;
          }
          tourGuideRef.value.start([
            {
              content: t('tourGuide.communityCreated.step1Text'),
              title: t('tourGuide.communityCreated.step1Title'),
              id: 'tg-templates-menu',
            },
            {
              content: t('tourGuide.communityCreated.step2Text'),
              title: t('tourGuide.communityCreated.step2Title'),
              id: 'tg-navBar-participation',
            },
            {
              content: t('tourGuide.communityCreated.step3Text'),
              title: t('tourGuide.communityCreated.step3Title'),
              id: 'tg-members-menu',
            },
            {
              content: t('tourGuide.communityCreated.step4Text'),
              title: t('tourGuide.communityCreated.step4Title'),
              id: 'tg-channel-menu',
            },
            {
              content: t('tourGuide.communityCreated.step5Text'),
              title: t('tourGuide.communityCreated.step5Title'),
              id: 'tg-quests-menu',
            },
            {
              content: t('tourGuide.communityCreated.step6Text'),
              title: t('tourGuide.communityCreated.step6Title'),
              id: 'tg-sub-communities-menu',
            },
          ]);
        }, 1000);
        return;
      }
    }, { immediate: true });

    // Watcher pour déclencher le tour onboarding
    watch([currentIssuerInfo, route], () => {
      const currentDid = currentIssuerDid.value;
      if (!currentDid) return;

      const issuerState = currentIssuerInfo.value?.state;
      
      // 🚫 Ne PAS lancer le tour d'onboarding si le tour de création de communauté doit se lancer
      const tourPendingKey = `tour_pending_community_created_${currentDid}`;
      const tourPending = localStorage.getItem(tourPendingKey);
      const forceRelaunch = process.env.VUE_APP_FORCE_COMMUNITY_TOUR === 'true';
      const tourCompletedCommunityKey = `tour_completed_community_created_${currentDid}`;
      const tourCompletedCommunity = localStorage.getItem(tourCompletedCommunityKey);
      const shouldLaunchCommunityTour = tourPending || (forceRelaunch && !tourCompletedCommunity);
      
      // Le tour de communauté a la priorité (peu importe la page)
      if (shouldLaunchCommunityTour) {
        return; // Le tour de communauté a la priorité
      }
      
      // Vérifier si le tour d'onboarding a déjà été complété
      const tourKey = `tour_completed_onboarding_${currentDid}`;
      const tourCompletedOnboarding = localStorage.getItem(tourKey);

      // Déclencher si prekey ET sur brand-analyzer
      if (issuerState === 'prekey' && 
          !tourCompletedOnboarding && 
          route.path === '/brand-analyzer') {
        // Petit délai pour laisser la page se charger
        setTimeout(() => {
          if (isMobileViewport.value) {
            showOnboardingModal.value = false;
            return;
          }
          showOnboardingModal.value = true;
        }, 1000);
      }
    }, { immediate: true });

    onMounted(() => {
      const bootPath = router.currentRoute.value?.path;
      if (!isLightBootPublicPath(bootPath)) {
        api.getApiVersion().then((res) => {
          apiVersion.value = res.data.version;
          if (typeof window !== 'undefined') {
            window.__MYDID_API_VERSION__ = res.data.version;
          }
        });
      }
      checkDarkMode();
    });

    // Browser language detection with best practices
    function detectBrowserLanguage() {
      const supportedLanguages = ['en', 'fr'];

      // Use navigator.languages if available (modern browsers)
      if (navigator.languages && navigator.languages.length > 0) {
        for (const lang of navigator.languages) {
          const baseLang = lang.split('-')[0].toLowerCase();
          if (supportedLanguages.includes(baseLang)) {
            return baseLang;
          }
        }
      }

      // Fallback to navigator.language (older browsers)
      if (navigator.language) {
        const baseLang = navigator.language.split('-')[0].toLowerCase();
        if (supportedLanguages.includes(baseLang)) {
          return baseLang;
        }
      }

      // Default fallback
      return 'en';
    }

    function toggleCollapse() {
      const newCollapsedState = !isNavbarCollapsed.value;
      store.commit('setNavbarCollapsed', newCollapsedState);
      cookies.set('navbarCollapsed', newCollapsedState.toString(), -1);
    }

    onMounted(() => {
      hideInitialLoader();

      // 🔄 Migration : Utiliser 'locale' au lieu de 'lang'
      const oldLang = localStorage.getItem('lang');
      if (oldLang && !localStorage.getItem('locale')) {
        localStorage.setItem('locale', oldLang);
        localStorage.removeItem('lang');
      }

      const savedLang = localStorage.getItem('locale');
      if (savedLang) {
        locale.value = savedLang;
      } else {
        const browserLang = detectBrowserLanguage();
        if (browserLang) {
          locale.value = browserLang;
          // NE PAS sauvegarder : l'utilisateur peut changer dans Paramètres
        }
      }

      // Synchroniser l'attribut lang du document (a11y, SEO, cohérence avec vue-i18n)
      syncDocumentLang(locale.value);

      // Réagir au changement de langue du navigateur : recharger la page (signin/signup, etc. à jour)
      const onLanguageChange = () => {
        if (localStorage.getItem('locale')) return;
        const newLang = getBrowserLocaleOnly();
        if (newLang && newLang !== locale.value) {
          window.location.reload();
        }
      };
      window.addEventListener('languagechange', onLanguageChange);
      onBeforeUnmount(() => {
        window.removeEventListener('languagechange', onLanguageChange);
      });

      // Initialize navbar collapsed state from cookies
      const savedCollapsed = cookies.get('navbarCollapsed') === 'true';
      store.commit('setNavbarCollapsed', savedCollapsed);

    });

    // Garder document.documentElement.lang en phase avec la locale (changement dans Paramètres > Langue)
    watch(locale, (newVal) => {
      syncDocumentLang(newVal);
    });

    async function recoverSession() {
      startBootRecoveryWatchdog('recoverSession');
      try {
        // Récupérer la session via /auth/me (cookie HttpOnly lu côté serveur)
        const session = await withTimeout(
          store.dispatch('initSession'),
          SESSION_RECOVERY_TIMEOUT_MS,
          `BOOT_RECOVER_SESSION_TIMEOUT_${SESSION_RECOVERY_TIMEOUT_MS}MS`
        );

        if (session && !session.isTmp) {
          store.commit('setCurrentIssuerDid', session.did);
          store.commit('setCurrentAddress', utils.didToAddress(session.did));
          store.commit('setIsWeb2Connexion', session.web2);
          store.commit('setConnexionEmail', session.connexionEmail);
          store.commit('setIsAdmin', session.isAdmin);
          store.dispatch('getTemplateLists');
          store.dispatch('getIssuerInfo');
          return;
        }

        store.commit('resetIssuerState');
        store.commit('clearSessionInfo');
      } catch (error) {
        console.error('[BOOT] recoverSession failed:', error?.message || error);
        store.commit('resetIssuerState');
        store.commit('clearSessionInfo');
      } finally {
        stopBootRecoveryWatchdog();
      }
    }

    // // redirect when issuer changes
    // watch(currentIssuerDid, () => {
    //   if (!currentIssuerDid.value) {
    //     const forceSignup = cookies.get('signup');
    //     if (forceSignup) {
    //       cookies.remove('signup');
    //       router.replace('/signin?new=1');
    //     } else router.push('/signin');
    //     return;
    //   }
    // });

    // wait for router to be ready before checking the route
    // Pages purement publiques : inutile d'appeler /auth/me, pas de session attendue
    const noAuthPages = [
      '/',
      '/signin',
      '/signup',
      '/protected',
      '/expired',
      '/auth',
    ];
    startBootRecoveryWatchdog('router.isReady');
    router.isReady().then(async () => {
      const currentPath = router.currentRoute.value.path;
      if (!noAuthPages.includes(currentPath)) {
        await recoverSession();
        return;
      }
      stopBootRecoveryWatchdog();
    }).catch((error) => {
      console.error('[BOOT] router.isReady failed:', error?.message || error);
      stopBootRecoveryWatchdog();
    });

    // onboarding - Désactivé : la logique est gérée dans la navbar avec des conditions
    // watch([currentIssuerDid, templateListLoading, route], () => {
    //   if (
    //     currentIssuerDid.value &&
    //     currentIssuerInfo.value &&
    //     !templateListLoading.value &&
    //     membershipTemplateList.value &&
    //     membershipTemplateList.value.length == 0 &&
    //     isAdmin.value
    //   ) {
    //     // Skip redirection if user is on community-create or brand-analyzer pages
    //     if (route.path === '/community-create' || route.path.startsWith('/brand-analyzer')) {
    //       return;
    //     }
    //     
    //     if (
    //       currentIssuerInfo.value.state != 'prekey' &&
    //       !route.path.startsWith('/templates/membership') &&
    //       !route.path.startsWith('/auth')
    //     )
    //       router.push('/templates/membership');
    //     else if (
    //       currentIssuerInfo.value.state == 'prekey' &&
    //       route.path.startsWith('/templates')
    //     )
    //       router.push('/brand-analyzer');
    //   }
    // });


    return {
      currentIssuerDid,
      currentAddress,
      areVerticalBarsDisplayed,
      isMarketingPublicRoute,
      isNavbarCollapsed,
      isNavbarOpen,
      mobileNavOpen,
      isMobileViewport,
      currentLocale,
      route,
      router,
      toggleCollapse,
      apiVersion,
      isSetting,
      isSettingsStandaloneRoute,
      settingsHeaderTabs,
      isSettingsHeaderTabActive,
      isLegalRoute,
      legalSubTabs,
      isLegalSubTabActive,
      contentAreaPaddingClass,
      mainScrollContainerRef,
      currentIssuerInfo,
      showSubscriptionFlow,
      handleSubscriptionSuccess,
      toggleDarkMode,
      isDarkMode,
      showOnboardingModal,
      tourGuideRef,
      handleOnboardingStart,
      handleOnboardingSkip,
      handleTourFinish,
      sendModalTemplate,
      sendModalTemplateList,
      closeSendModal,
      refreshSendModalTemplate,
      refreshSendModalTemplateList,
      isPlanReminderLoading,
      planReminderLabel,
      isFreePlan,
      planPopupTitle,
      planPopupMessage,
      planPopupCard,
      showPlanReminderPopup,
      goToSubscription,
      dismissPlanPopup,
      showPasskeyReminderPopup,
      dismissPasskeyReminderPopup,
      openPasskeySetupFromReminder,
    };
  },
  components: {
    MarketingNavbar,
    NavBar,
    CookieConsent,
    SubscriptionFlow,
    TourOnboardingModal,
    TourGuide,
    BasicSmModal,
    TemplateSend,
  },
};
</script>
